import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState } from 'react';
import { useParams, useNavigate } from '@tanstack/react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2, ArrowLeft } from 'lucide-react';
import { useAdminUserDetail } from '@/features/admin/hooks/useAdminUserDetail';
import { useAdminUserMessages } from '@/features/admin/hooks/useAdminUserMessages';
import { adminApi } from '@/features/admin/api/adminApi';
import { adminKeys } from '@/features/admin/api/adminQueryKeys';
import { Dialog, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/Dialog';
export function AdminUserDetailPage() {
    const { userId } = useParams({ strict: false });
    const navigate = useNavigate();
    const { data, isLoading, error } = useAdminUserDetail(userId);
    if (isLoading) {
        return (_jsxs("div", { className: "admin-spinner", style: { padding: '40px' }, children: [_jsx(Loader2, { size: 20 }), _jsx("span", { children: "Loading user..." })] }));
    }
    if (error || !data) {
        return _jsx("div", { className: "admin-error", children: "Failed to load user." });
    }
    return (_jsxs("div", { className: "admin-dashboard", children: [_jsxs("button", { className: "admin-btn", onClick: () => navigate({ to: '/admin/users' }), style: { display: 'inline-flex', alignItems: 'center', gap: '6px', marginBottom: '16px' }, children: [_jsx(ArrowLeft, { size: 14 }), "Back to users"] }), _jsx(IdentityHeader, { user: data }), _jsxs("section", { style: { marginTop: '32px' }, children: [_jsx(SectionHeading, { children: "Activity" }), _jsx(ActivityGrid, { counts: data.activity })] }), _jsxs("section", { style: { marginTop: '32px' }, children: [_jsx(SectionHeading, { children: "Limits" }), _jsx(LimitsBlock, { userId: userId, limits: data.limits })] }), _jsxs("section", { style: { marginTop: '32px' }, children: [_jsxs(SectionHeading, { children: ["Parties (", data.parties.length, ")"] }), _jsx(PartiesTable, { parties: data.parties })] }), _jsxs("section", { style: { marginTop: '32px' }, children: [_jsx(SectionHeading, { children: "Messages" }), _jsx(MessagesTimeline, { userId: userId })] })] }));
}
function MessagesTimeline({ userId }) {
    const [page, setPage] = useState(1);
    const { data, isLoading } = useAdminUserMessages(userId, page);
    if (isLoading) {
        return (_jsxs("div", { className: "admin-spinner", children: [_jsx(Loader2, { size: 20 }), _jsx("span", { children: "Loading messages..." })] }));
    }
    if (!data || data.items.length === 0) {
        return _jsx("div", { className: "admin-empty", children: "No messages." });
    }
    const totalPages = Math.max(1, Math.ceil(data.total / data.pageSize));
    return (_jsxs(_Fragment, { children: [_jsx("div", { style: { display: 'flex', flexDirection: 'column', gap: '8px' }, children: data.items.map((m) => (_jsx(MessageRow, { message: m }, m.messageId))) }), _jsxs("div", { className: "admin-pagination", style: { marginTop: '16px' }, children: [_jsxs("span", { style: {
                            fontSize: '0.8125rem',
                            color: 'var(--color-text-muted)',
                            fontFamily: 'var(--font-family-body)',
                            marginRight: '12px',
                        }, children: ["Page ", page, " of ", totalPages, " \u00B7 ", data.total.toLocaleString(), " messages"] }), _jsx("button", { className: "admin-btn", onClick: () => setPage((p) => Math.max(1, p - 1)), disabled: page === 1, children: "Previous" }), _jsx("button", { className: "admin-btn", onClick: () => setPage((p) => p + 1), disabled: !data.hasMore, children: "Next" })] })] }));
}
function MessageRow({ message }) {
    return (_jsxs("div", { style: {
            padding: '10px 12px',
            background: 'var(--color-surface)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-md)',
        }, children: [_jsxs("div", { style: { display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '6px' }, children: [message.partyName && _jsx(Badge, { tone: "muted", children: message.partyName }), message.workTitle && _jsx(Badge, { tone: "muted", children: message.workTitle }), message.discussionTitle && _jsx(Badge, { tone: "muted", children: message.discussionTitle }), message.isSpoiler && _jsx(Badge, { tone: "warn", children: "Spoiler" }), _jsxs("span", { style: {
                            fontSize: '0.75rem',
                            color: 'var(--color-text-muted)',
                            fontFamily: 'var(--font-family-body)',
                            marginLeft: 'auto',
                        }, children: [new Date(message.createdAt).toLocaleString(), message.editedAt && ' (edited)'] })] }), _jsx("div", { style: {
                    fontSize: '0.875rem',
                    fontFamily: 'var(--font-family-body)',
                    color: 'var(--color-text)',
                    whiteSpace: 'pre-wrap',
                }, children: message.content ?? _jsx("em", { style: { color: 'var(--color-text-muted)' }, children: "(empty)" }) })] }));
}
function IdentityHeader({ user, }) {
    const initials = (user.displayName || user.email || '?').slice(0, 2).toUpperCase();
    return (_jsxs("div", { className: "admin-stat-card", style: { display: 'flex', gap: '16px', alignItems: 'center' }, children: [user.avatarUrl ? (_jsx("img", { src: user.avatarUrl, alt: "", style: { width: 56, height: 56, borderRadius: '50%' } })) : (_jsx("div", { style: {
                    width: 56,
                    height: 56,
                    borderRadius: '50%',
                    background: user.avatarColor ?? 'var(--color-surface-sunken)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'var(--color-text)',
                    fontFamily: 'var(--font-family-heading)',
                    fontWeight: 700,
                    fontSize: '1.125rem',
                }, children: initials })), _jsxs("div", { style: { flex: 1 }, children: [_jsx("h2", { style: {
                            fontFamily: 'var(--font-family-heading)',
                            fontSize: '1.25rem',
                            fontWeight: 700,
                            color: 'var(--color-text)',
                            margin: 0,
                        }, children: user.displayName ?? 'No display name' }), _jsx("p", { style: {
                            fontSize: '0.875rem',
                            color: 'var(--color-text-muted)',
                            fontFamily: 'var(--font-family-body)',
                            margin: '2px 0 8px',
                        }, children: user.email ?? '—' }), _jsxs("div", { style: { display: 'flex', flexWrap: 'wrap', gap: '6px' }, children: [_jsx(Badge, { tone: user.emailConfirmed ? 'ok' : 'warn', children: user.emailConfirmed ? 'Email confirmed' : 'Email unconfirmed' }), _jsx(Badge, { tone: user.onboardingComplete ? 'ok' : 'warn', children: user.onboardingComplete ? 'Onboarded' : 'Not onboarded' }), _jsxs(Badge, { tone: "muted", children: ["Joined ", formatDate(user.createdAt)] }), _jsx(Badge, { tone: "muted", children: user.lastSeenAt ? `Last active ${formatRelative(user.lastSeenAt)}` : 'Never seen' })] })] })] }));
}
function SectionHeading({ children }) {
    return (_jsx("h2", { style: {
            fontFamily: 'var(--font-family-heading)',
            fontSize: '1.125rem',
            fontWeight: 700,
            color: 'var(--color-text)',
            marginBottom: '12px',
        }, children: children }));
}
function ActivityGrid({ counts }) {
    const tiles = [
        ['Works added', counts.worksAdded],
        ['Ratings', counts.ratings],
        ['Messages', counts.messages],
        ['Want to Watch', counts.wantToWatch],
        ['Watching', counts.watching],
        ['Watched', counts.watched],
    ];
    return (_jsx("div", { className: "admin-stats-grid", children: tiles.map(([label, value]) => (_jsxs("div", { className: "admin-stat-card", children: [_jsx("h3", { children: label }), _jsx("div", { className: "admin-stat-rows", children: _jsxs("div", { className: "admin-stat-row", children: [_jsx("span", { className: "admin-stat-label", children: "Total" }), _jsx("span", { className: "admin-stat-value", children: value.toLocaleString() })] }) })] }, label))) }));
}
function LimitsBlock({ userId, limits, }) {
    const qc = useQueryClient();
    const [open, setOpen] = useState(false);
    const [maxParties, setMaxParties] = useState(limits?.maxParties ?? 1);
    const [maxPartyMembers, setMaxPartyMembers] = useState(limits?.maxPartyMembers ?? 20);
    const [maxPartyWorks, setMaxPartyWorks] = useState(limits?.maxPartyWorks ?? 50);
    const update = useMutation({
        mutationFn: () => adminApi.updateUserLimits(userId, { maxParties, maxPartyMembers, maxPartyWorks }),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: adminKeys.userDetail(userId) });
            qc.invalidateQueries({ queryKey: adminKeys.userLimits(userId) });
            setOpen(false);
        },
    });
    function openDialog() {
        if (limits) {
            setMaxParties(limits.maxParties);
            setMaxPartyMembers(limits.maxPartyMembers);
            setMaxPartyWorks(limits.maxPartyWorks);
        }
        setOpen(true);
    }
    if (!limits) {
        return (_jsx("div", { className: "admin-stat-card", children: _jsx("p", { style: { color: 'var(--color-text-muted)', fontSize: '0.875rem' }, children: "No per-user limits set. System defaults apply." }) }));
    }
    return (_jsxs(_Fragment, { children: [_jsxs("div", { className: "admin-stat-card", children: [_jsxs("div", { className: "admin-stat-rows", children: [_jsxs("div", { className: "admin-stat-row", children: [_jsx("span", { className: "admin-stat-label", children: "Max Parties" }), _jsx("span", { className: "admin-stat-value", children: limits.maxParties })] }), _jsxs("div", { className: "admin-stat-row", children: [_jsx("span", { className: "admin-stat-label", children: "Max Party Members" }), _jsx("span", { className: "admin-stat-value", children: limits.maxPartyMembers })] }), _jsxs("div", { className: "admin-stat-row", children: [_jsx("span", { className: "admin-stat-label", children: "Max Party Works" }), _jsx("span", { className: "admin-stat-value", children: limits.maxPartyWorks })] })] }), _jsx("div", { style: { marginTop: '12px' }, children: _jsx("button", { className: "admin-btn admin-btn--active", onClick: openDialog, children: "Edit Limits" }) })] }), _jsxs(Dialog, { open: open, onOpenChange: setOpen, children: [_jsx(DialogHeader, { children: _jsx(DialogTitle, { children: "Edit User Limits" }) }), _jsxs("div", { style: { display: 'flex', flexDirection: 'column', gap: '14px' }, children: [_jsx(LimitField, { label: "Max Parties", value: maxParties, onChange: setMaxParties }), _jsx(LimitField, { label: "Max Party Members", value: maxPartyMembers, onChange: setMaxPartyMembers }), _jsx(LimitField, { label: "Max Party Works", value: maxPartyWorks, onChange: setMaxPartyWorks })] }), _jsxs(DialogFooter, { children: [_jsx("button", { className: "admin-btn", onClick: () => setOpen(false), children: "Cancel" }), _jsx("button", { className: "admin-btn admin-btn--active", onClick: () => update.mutate(), disabled: update.isPending, children: update.isPending ? 'Saving...' : 'Save' })] })] })] }));
}
function LimitField({ label, value, onChange, }) {
    return (_jsxs("div", { style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }, children: [_jsx("label", { style: {
                    fontSize: '0.875rem',
                    fontFamily: 'var(--font-family-body)',
                    color: 'var(--color-text)',
                    fontWeight: 500,
                }, children: label }), _jsx("input", { type: "number", min: 1, value: value, onChange: (e) => onChange(Math.max(1, parseInt(e.target.value) || 1)), style: {
                    width: '80px',
                    padding: '6px 10px',
                    fontSize: '0.875rem',
                    fontFamily: 'var(--font-family-body)',
                    background: 'var(--color-surface-sunken)',
                    border: '1px solid var(--color-border)',
                    borderRadius: 'var(--radius-md)',
                    color: 'var(--color-text)',
                    textAlign: 'right',
                } })] }));
}
function PartiesTable({ parties }) {
    if (parties.length === 0) {
        return _jsx("div", { className: "admin-empty", children: "Not a member of any parties." });
    }
    return (_jsxs("table", { className: "admin-table", children: [_jsx("thead", { children: _jsxs("tr", { children: [_jsx("th", { children: "Party" }), _jsx("th", { children: "Role" }), _jsx("th", { children: "Joined" }), _jsx("th", { children: "Works added" }), _jsx("th", { children: "Ratings" }), _jsx("th", { children: "Messages" })] }) }), _jsx("tbody", { children: parties.map((p) => (_jsxs("tr", { children: [_jsx("td", { children: p.name }), _jsx("td", { children: p.isOwner ? 'Owner' : 'Member' }), _jsx("td", { children: formatDate(p.joinedAt) }), _jsx("td", { children: p.worksAdded }), _jsx("td", { children: p.ratings }), _jsx("td", { children: p.messages })] }, p.partyId))) })] }));
}
function Badge({ children, tone }) {
    const colors = {
        ok: { bg: 'rgba(34, 197, 94, 0.1)', fg: 'rgb(74, 222, 128)' },
        warn: { bg: 'rgba(251, 191, 36, 0.1)', fg: 'rgb(251, 191, 36)' },
        muted: { bg: 'var(--color-surface-sunken)', fg: 'var(--color-text-muted)' },
    }[tone];
    return (_jsx("span", { style: {
            display: 'inline-block',
            padding: '2px 8px',
            fontSize: '0.75rem',
            fontFamily: 'var(--font-family-body)',
            background: colors.bg,
            color: colors.fg,
            borderRadius: 'var(--radius-sm, 4px)',
        }, children: children }));
}
function formatDate(iso) {
    return new Date(iso).toLocaleDateString();
}
function formatRelative(iso) {
    const d = new Date(iso);
    const diffMs = Date.now() - d.getTime();
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1)
        return 'just now';
    if (mins < 60)
        return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24)
        return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 30)
        return `${days}d ago`;
    return d.toLocaleDateString();
}
